import React, {createContext, useContext, useEffect, useId, useReducer, useRef} from "react";
import api from '../components/api';
import {facetRequest, mergeFacets} from '../components/explore/deferredFacetRequests';
import isEqual from 'lodash/isEqual';

const initialState = {
    aggs: {}, total: null, allAssetCounts: {}, coords: [], mapData: null,
    searchId: null, params: null, ready: false, organizationId: null, scopeVersion: 0,
    consumers: {}, available: [], loaded: [], loading: [], failed: [], onSettled: null, facetVersion: 0,
};

function searchScope(params) {
    const {snapshot_timestamp, selected_asset_ids, ...scope} = params || {};
    // Selection affects counts only when it is itself a search filter.
    if(scope.selection) scope.selected_asset_ids = selected_asset_ids;
    return scope;
}

const AggsStateContext = createContext(initialState);
const AggsDispatchContext = createContext(() => {});

export const aggsReducer = (state, action) => {
    if(action.searchId != null && action.type !== 'searchStarted' && action.searchId !== state.searchId) return state;
    if(action.facetVersion != null && action.facetVersion !== state.facetVersion) return state;
    switch(action.type) {
        case 'registerFacets':
            return {...state, consumers: {...state.consumers, [action.id]: action.keys}};
        case 'unregisterFacets': {
            const consumers = {...state.consumers};
            delete consumers[action.id];
            return {...state, consumers};
        }
        case 'searchStarted': {
            const retain = action.background && state.params &&
                state.organizationId === action.organizationId &&
                isEqual(searchScope(state.params), searchScope(action.params));
            return {...initialState, consumers: state.consumers, searchId: action.searchId,
                params: action.params, organizationId: action.organizationId,
                scopeVersion: retain ? state.scopeVersion : state.scopeVersion + 1,
                ...(retain ? {aggs: state.aggs, available: state.available, allAssetCounts: state.allAssetCounts,
                    total: state.total, coords: state.coords, mapData: state.mapData} : {})};
        }
        case 'searchStopped':
            return {...initialState, consumers: state.consumers, scopeVersion: state.scopeVersion + 1};
        case 'searchLoaded':
            return {...state, ready: true, total: action.total, coords: action.coords || [],
                mapData: action.mapData || null, onSettled: action.onSettled || null};
        case 'facetsLoading':
            return {...state, loading: [...state.loading, ...action.keys]};
        case 'facetsLoaded':
            return {...state, aggs: mergeFacets(state.aggs, action.data.aggs || {}, action.keys),
                allAssetCounts: action.data.all_asset_counts || state.allAssetCounts,
                available: [...new Set([...state.available, ...action.keys])],
                loaded: [...new Set([...state.loaded, ...action.keys])],
                loading: state.loading.filter(key => !action.keys.includes(key))};
        case 'facetsFailed':
            return {...state, failed: [...new Set([...state.failed, ...action.keys])],
                loading: state.loading.filter(key => !action.keys.includes(key))};
        case 'refreshFacets':
            return {...state, loaded: [], loading: [], failed: [], facetVersion: state.facetVersion + 1};
        case 'aggsLoaded':
            return {...state, aggs: action.aggs, total: action.total,
                allAssetCounts: action.allAssetCounts, coords: action.coords, mapData: action.mapData, params: action.params};
        case 'incrementTotal': return {...state, total: state.total + 1};
        case 'setTotal': return {...state, total: action.total};
        case 'remove': return {...state, total: state.total - action.count};
        default: return state;
    }
};

function AggsProvider({children}) {
    const [state, dispatch] = useReducer(aggsReducer, initialState);
    const controllers = useRef(new Set());
    const requested = [...new Set(Object.values(state.consumers).flat())].sort();
    const missing = requested.filter(key => !state.loaded.includes(key) && !state.loading.includes(key) && !state.failed.includes(key));

    useEffect(() => () => {
        controllers.current.forEach(controller => controller.abort());
        controllers.current.clear();
    }, [state.searchId, state.facetVersion]);

    useEffect(() => {
        if(!state.ready || !state.params || !missing.length) return;
        const keys = missing;
        const searchId = state.searchId;
        const facetVersion = state.facetVersion;
        const controller = new AbortController();
        controllers.current.add(controller);
        dispatch({type: 'facetsLoading', searchId, facetVersion, keys});
        api.post('/api/assets/search', facetRequest(state.params, keys), {signal: controller.signal}).then(({data}) => {
            if(data.error || !data.aggs || Array.isArray(data.aggs)) throw new Error('Facet search failed');
            dispatch({type: 'facetsLoaded', searchId, facetVersion, keys, data});
        }).catch(() => {
            if(!controller.signal.aborted) dispatch({type: 'facetsFailed', searchId, facetVersion, keys});
        }).finally(() => controllers.current.delete(controller));
    }, [state.ready, state.params, state.searchId, state.facetVersion, missing.join('\n')]);

    // Live-refresh callers can await visible counts without delaying grid rendering.
    useEffect(() => {
        if(state.ready && !missing.length && !requested.some(key => state.loading.includes(key))) {
            state.onSettled?.({failed: requested.some(key => state.failed.includes(key))});
        }
    }, [state.ready, state.onSettled, missing.length, requested.join('\n'), state.loading, state.failed]);
    useEffect(() => () => state.onSettled?.({cancelled: true}), [state.onSettled]);

    return <AggsStateContext.Provider value={state}>
        <AggsDispatchContext.Provider value={dispatch}>{children}</AggsDispatchContext.Provider>
    </AggsStateContext.Provider>;
}

function useAggsState() { return useContext(AggsStateContext); }
function useAggsDispatch() { return useContext(AggsDispatchContext); }

// Register only mounted consumers; hidden panels should unmount their contents.
function useFacets(keys = []) {
    const id = useId();
    const state = useAggsState();
    const dispatch = useAggsDispatch();
    const signature = [...new Set(keys.filter(Boolean))].sort().join('\n');
    useEffect(() => {
        if(!signature) return;
        dispatch({type: 'registerFacets', id, keys: signature.split('\n')});
        return () => dispatch({type: 'unregisterFacets', id});
    }, [dispatch, id, signature]);
    const requested = signature ? signature.split('\n') : [];
    return {
        // Cached counts may be displayed while fresh requests are still pending.
        available: requested.every(key => state.available.includes(key)),
        ready: requested.every(key => state.loaded.includes(key)),
        failed: requested.some(key => state.failed.includes(key)),
        retry: () => dispatch({type: 'refreshFacets'}),
    };
}

export {AggsProvider, useAggsState, useAggsDispatch, useFacets};
